import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DescriptionIcon from '@mui/icons-material/Description'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import LanguageIcon from '@mui/icons-material/Language'
import RemoveIcon from '@mui/icons-material/Remove'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import SearchIcon from '@mui/icons-material/Search'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material'
import { ServiceMenuItem, useServiceMenu } from 'api/aws/service-menu/useServiceMenu'
import {
  useAddStrategyItems,
  useCreateStrategy,
  useDeleteStrategy,
  useStrategies,
  useStrategyRecommend,
  useUpdateStrategy,
  useUploadDocument,
} from 'api/aws/strategy-builder/useStrategyBuilder'
import { baseUrl } from 'api/config'
import { useSearchData } from 'api/search/useSearchData'
import axios from 'axios'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useCallback, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import AISherpaDialog from './AISherpaDialog'
import { BuilderProvider, useBuilderContext } from './StrategyBuilderContext'
import StrategyDeployDialog from './StrategyDeployDialog'
import { AiRecommendation, ClientSize, FullStrategy, ServiceStage, Strategy, StrategyItem } from './types'

// ── Constants ────────────────────────────────────────────────────────────────

const CLIENT_SIZES: ClientSize[] = ['SMB', 'Mid-Market', 'Enterprise']
const SERVICE_STAGES: ServiceStage[] = ['Launch', 'Growth', 'Maturity', 'Optimization']

const SERVICE_LINE_OPTIONS = [
  { value: 'seo', label: 'SEO' },
  { value: 'ppc', label: 'PPC' },
  { value: 'web', label: 'Web' },
  { value: 'social', label: 'Social' },
  { value: 'creative', label: 'Creative' },
  { value: 'emm', label: 'Email' },
  { value: 'ana', label: 'Analytics' },
  { value: 'ux', label: 'UX' },
  { value: 'int', label: 'Integration' },
]

const PRIORITY_COLORS: Record<string, 'success' | 'warning' | 'default'> = {
  high: 'success',
  medium: 'warning',
  low: 'default',
}

function generateMonthOptions(count = 12): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = []
  const now = new Date()
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1)
    options.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    })
  }
  return options
}

// ── Saved Strategies Dialog ──────────────────────────────────────────────────

interface SavedStrategiesDialogProps {
  open: boolean
  onClose: () => void
  onLoad: (strategy: Strategy, items: StrategyItem[]) => void
}

const SavedStrategiesDialog = ({ open, onClose, onLoad }: SavedStrategiesDialogProps) => {
  const { data: strategies = [], isLoading } = useStrategies()

  const deleteMutation = useDeleteStrategy()
  const { showSnackbar } = useSnackbarContext()
  const [searchQuery, setSearchQuery] = useState('')
  const [loadingId, setLoadingId] = useState<string | null>(null)

  // Group strategies by client name
  const grouped = useMemo(() => {
    let list = strategies
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      list = list.filter(
        s =>
          s.clientName?.toLowerCase().includes(q) ||
          s.name?.toLowerCase().includes(q) ||
          s.strategyGoal?.toLowerCase().includes(q) ||
          s.status?.toLowerCase().includes(q)
      )
    }
    // Sort newest first
    list = [...list].sort((a, b) =>
      (b.updatedAt ?? b.createdAt ?? '').localeCompare(a.updatedAt ?? a.createdAt ?? '')
    )
    // Group by client name
    const groups: Record<string, Strategy[]> = {}
    for (const s of list) {
      const key = s.clientName || 'Unnamed'
      if (!groups[key]) groups[key] = []
      groups[key].push(s)
    }
    return groups
  }, [strategies, searchQuery])

  const groupEntries = useMemo(() => Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)), [grouped])
  const totalCount = useMemo(() => groupEntries.reduce((sum, [, items]) => sum + items.length, 0), [groupEntries])
  const [expandedClient, setExpandedClient] = useState<string | null>(null)

  const handleLoad = useCallback(
    async (strategy: Strategy) => {
      setLoadingId(strategy.strategyId)
      try {
        const resp = await axios.get<FullStrategy>(`${baseUrl}/api/strategy-builder/${strategy.strategyId}`)
        const full = resp.data
        onLoad(full, full.items || [])
        onClose()
        showSnackbar(`Loaded "${strategy.clientName}" strategy`, 'success')
      } catch {
        showSnackbar('Failed to load strategy', 'error')
      } finally {
        setLoadingId(null)
      }
    },
    [onLoad, onClose, showSnackbar]
  )

  const handleDelete = useCallback(
    async (e: React.MouseEvent, id: string) => {
      e.stopPropagation()
      try {
        await deleteMutation.mutateAsync(id)
        showSnackbar('Strategy deleted', 'success')
      } catch {
        showSnackbar('Failed to delete strategy', 'error')
      }
    },
    [deleteMutation, showSnackbar]
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: 'background.paper' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FolderOpenIcon fontSize="small" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Saved Strategies
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 2 }}>
        <TextField
          placeholder="Search by client, name, or goal..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : totalCount === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {searchQuery ? 'No strategies match your search' : 'No saved strategies yet'}
          </Typography>
        ) : (
          <Stack spacing={0.5} sx={{ maxHeight: 500, overflowY: 'auto' }}>
            {groupEntries.map(([clientName, items]) => (
              <Box key={clientName}>
                {/* Client group header */}
                <Box
                  onClick={() => setExpandedClient(prev => prev === clientName ? null : clientName)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.75,
                    px: 1.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: 'background.default',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <FolderOpenIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                    {clientName}
                  </Typography>
                  <Chip label={`${items.length}`} size="small" sx={{ height: 18, fontSize: '0.65rem', minWidth: 24 }} />
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 18,
                      color: 'text.secondary',
                      transform: expandedClient === clientName ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s',
                    }}
                  />
                </Box>

                {/* Expanded strategy list for this client */}
                {expandedClient === clientName && (
                  <Stack spacing={0.5} sx={{ pl: 2, pt: 0.5 }}>
                    {items.map(strategy => (
                      <Box
                        key={strategy.strategyId}
                        onClick={() => handleLoad(strategy)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          py: 0.75,
                          px: 1.5,
                          borderRadius: 1,
                          cursor: 'pointer',
                          border: 1,
                          borderColor: 'divider',
                          '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
                          transition: 'all 0.15s ease',
                          opacity: loadingId === strategy.strategyId ? 0.6 : 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                            {strategy.name || `${clientName} Strategy`}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(strategy.updatedAt ?? strategy.createdAt).toLocaleDateString()}
                            </Typography>
                            <Chip
                              label={`${strategy.itemCount || 0} items`}
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                            <Chip
                              label={`${strategy.totalPoints || 0} pts`}
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                            {strategy.status && (
                              <Chip
                                label={strategy.status}
                                size="small"
                                color={
                                  strategy.status === 'deployed' ? 'success' :
                                  strategy.status === 'saved' ? 'primary' : 'default'
                                }
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Stack>
                        </Box>
                        {loadingId === strategy.strategyId ? (
                          <CircularProgress size={18} />
                        ) : (
                          <IconButton
                            size="small"
                            onClick={e => handleDelete(e, strategy.strategyId)}
                            disabled={deleteMutation.isPending}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ── Main Component ───────────────────────────────────────────────────────────

const StrategyBuilderInner = () => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const { state, dispatch } = useBuilderContext()
  const { showSnackbar } = useSnackbarContext()
  const { data: catalog = [] } = useServiceMenu()
  const { menu_orgs: organizations = [] } = useSearchData()
  const createMutation = useCreateStrategy()
  const recommendMutation = useStrategyRecommend()
  const addItemsMutation = useAddStrategyItems()
  const updateStrategyMutation = useUpdateStrategy()

  // Config state
  const [clientName, setClientName] = useState('')
  const [clientOrgId, setClientOrgId] = useState<string | null>(null)
  const [clientWebsite, setClientWebsite] = useState('')
  const [clientSize, setClientSize] = useState<ClientSize>('Mid-Market')
  const [serviceStage, setServiceStage] = useState<ServiceStage>('Growth')
  const [targetBudget, setTargetBudget] = useState<number>(20)
  const [strategyGoal, setStrategyGoal] = useState('')
  const [focusAreas, setFocusAreas] = useState<string[]>([])
  const [sherpaNotes, setSherpaNotes] = useState('')
  const [selectedMonths, setSelectedMonths] = useState<string[]>([])
  const [configExpanded, setConfigExpanded] = useState(true)

  // Reset strategy when client changes (if a strategy already exists)
  const handleClientChange = useCallback(
    (newName: string, newOrgId: string | null, newWebsite?: string) => {
      const hadStrategy = !!state.strategyId
      setClientName(newName)
      setClientOrgId(newOrgId)
      if (newWebsite !== undefined) setClientWebsite(newWebsite)

      // If a strategy was already created and the client changed, reset everything
      if (hadStrategy && newName !== state.strategy?.clientName) {
        dispatch({ type: 'RESET' })
        setAnalysisSummary('')
        setStrategyGoal('')
        setSherpaNotes('')
        setFocusAreas([])
        setStagedFiles([])
        setConfigExpanded(true)
      }
    },
    [state.strategyId, state.strategy?.clientName, dispatch]
  )

  // File upload
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [stagedFiles, setStagedFiles] = useState<File[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const uploadMutation = useUploadDocument()

  // Website analysis
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisSummary, setAnalysisSummary] = useState('')

  // File upload handlers
  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const files = Array.from(e.dataTransfer.files).filter(f =>
      ['.csv', '.pdf'].some(ext => f.name.toLowerCase().endsWith(ext))
    )
    if (files.length) setStagedFiles(prev => [...prev, ...files])
  }, [])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length) setStagedFiles(prev => [...prev, ...files])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  const removeStagedFile = useCallback((idx: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== idx))
  }, [])

  // Item browser
  const [search, setSearch] = useState('')
  const [deptFilter, setDeptFilter] = useState('All Depts')
  const [lineFilter, setLineFilter] = useState('All Lines')
  const [visibleCount, setVisibleCount] = useState(50)
  const [deployOpen, setDeployOpen] = useState(false)
  const [sherpaOpen, setSherpaOpen] = useState(false)

  const monthOptions = useMemo(() => generateMonthOptions(12), [])

  // Active catalog (non-deprecated)
  const activeCatalog = useMemo(() => catalog.filter(i => !i.isDeprecated && i.status !== 'Deprecated'), [catalog])

  // Saved strategies browser
  const [savedOpen, setSavedOpen] = useState(false)

  const handleLoadSaved = useCallback(
    (strategy: Strategy, items: StrategyItem[]) => {
      // Populate config fields
      setClientName(strategy.clientName || '')
      setClientSize(strategy.clientSize || 'Mid-Market')
      setServiceStage(strategy.serviceStage || 'Growth')
      setTargetBudget(strategy.targetPointBudget || 20)
      setStrategyGoal(strategy.strategyGoal || '')
      setFocusAreas(strategy.focusAreas || [])
      setSherpaNotes(strategy.sherpaNotes || '')
      setSelectedMonths(strategy.months || [])
      setClientOrgId(null)
      setClientWebsite('')
      setAnalysisSummary('')
      setStagedFiles([])

      // Load into builder state
      dispatch({ type: 'SET_STRATEGY', payload: strategy })
      dispatch({ type: 'SET_STRATEGY_ID', payload: strategy.strategyId })
      dispatch({ type: 'SET_SELECTED_ITEMS', payload: items })
      dispatch({ type: 'SET_CATALOG', payload: activeCatalog })
      dispatch({ type: 'SET_AI_RECOMMENDATIONS', payload: { recommendations: [], summary: '' } })

      // Collapse config
      setConfigExpanded(false)
    },
    [activeCatalog, dispatch]
  )

  // Unique departments and service lines from catalog
  const departments = useMemo(
    () => ['All Depts', ...new Set(activeCatalog.map(i => i.subcategory).filter(Boolean))].sort(),
    [activeCatalog]
  )
  const serviceLines = useMemo(
    () => ['All Lines', ...new Set(activeCatalog.map(i => i.service_line).filter(Boolean))].sort(),
    [activeCatalog]
  )

  // Filtered catalog
  const filteredCatalog = useMemo(() => {
    let items = activeCatalog
    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter(
        i =>
          i.title?.toLowerCase().includes(q) ||
          i.sku?.toLowerCase().includes(q) ||
          i.service_line?.toLowerCase().includes(q) ||
          i.category_name?.toLowerCase().includes(q)
      )
    }
    if (deptFilter !== 'All Depts') items = items.filter(i => i.subcategory === deptFilter)
    if (lineFilter !== 'All Lines') items = items.filter(i => i.service_line === lineFilter)
    return items
  }, [activeCatalog, search, deptFilter, lineFilter])

  // Reset visible count when filters change
  const visibleCatalog = useMemo(() => filteredCatalog.slice(0, visibleCount), [filteredCatalog, visibleCount])

  // Selected item tracking
  const { selectedItems, aiRecommendations, aiSummary, isLoadingAi, strategyId } = state
  const selectedIds = useMemo(() => new Set(selectedItems.map(i => i.menuItemId)), [selectedItems])
  const totalPoints = useMemo(() => selectedItems.reduce((sum, i) => sum + i.points * i.quantity, 0), [selectedItems])
  const totalValue = useMemo(() => selectedItems.reduce((sum, i) => sum + i.marketValue * i.quantity, 0), [
    selectedItems,
  ])

  // ── Website Analysis ─────────────────────────────────────────────────────

  // Analyze website and extract config data
  const analyzeWebsite = useCallback(async (url: string) => {
    const resp = await fetch(`${baseUrl}/api/agent/analyze-website`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    })
    const result = await resp.json()
    return result?.data
  }, [])

  // Apply analysis data to config fields — returns the resolved values for chaining
  const applyAnalysisData = useCallback(
    (data: any) => {
      let resolvedSize = clientSize
      let resolvedGoal = strategyGoal
      let resolvedFocus = [...focusAreas]
      let resolvedNotes = sherpaNotes

      if (data.clientSize) {
        const sizeMap: Record<string, ClientSize> = { smb: 'SMB', mid_market: 'Mid-Market', enterprise: 'Enterprise' }
        resolvedSize = sizeMap[data.clientSize] || 'Mid-Market'
        setClientSize(resolvedSize)
      }
      if (data.suggestedObjectives?.length) {
        const objToLine: Record<string, string> = {
          grow_organic_traffic: 'seo',
          improve_paid_performance: 'ppc',
          build_brand_awareness: 'social',
          develop_content_strategy: 'seo',
          fix_technical_issues: 'web',
          better_analytics: 'ana',
          increase_conversion_rate: 'ux',
        }
        const mapped = data.suggestedObjectives.map((o: string) => objToLine[o]).filter(Boolean)
        if (mapped.length) {
          resolvedFocus = [...new Set([...resolvedFocus, ...mapped])]
          setFocusAreas(resolvedFocus)
        }
      }
      if (data.aiInstructions) {
        resolvedGoal = data.aiInstructions
        setStrategyGoal(resolvedGoal)
      }
      const notes: string[] = []
      if (data.summary) notes.push(`Company: ${data.summary}`)
      if (data.industry) notes.push(`Industry: ${data.industry}`)
      if (data.businessModel) notes.push(`Business Model: ${data.businessModel}`)
      if (data.salesNotes) notes.push(`\nSales Notes:\n${data.salesNotes}`)
      if (notes.length) {
        resolvedNotes = notes.join('\n')
        setSherpaNotes(resolvedNotes)
      }

      setAnalysisSummary(data.summary || 'Analysis complete')
      return {
        clientSize: resolvedSize,
        strategyGoal: resolvedGoal,
        focusAreas: resolvedFocus,
        sherpaNotes: resolvedNotes,
      }
    },
    [clientSize, strategyGoal, focusAreas, sherpaNotes]
  )

  // Fetch & Analyze only (fills inputs, no strategy creation)
  const handleAnalyzeWebsite = useCallback(async () => {
    const url = clientWebsite.trim()
    if (!url) return
    setIsAnalyzing(true)
    setAnalysisSummary('')
    try {
      const data = await analyzeWebsite(url)
      if (data) {
        applyAnalysisData(data)
        showSnackbar('Website analyzed — inputs auto-filled', 'success')
      }
    } catch {
      showSnackbar('Failed to analyze website', 'error')
    } finally {
      setIsAnalyzing(false)
    }
  }, [clientWebsite, analyzeWebsite, applyAnalysisData, showSnackbar])

  // Full pipeline: Fetch → Auto-fill → Create Strategy → AI Recommendations → Auto-select
  const handleFetchAndBuild = useCallback(async () => {
    const url = clientWebsite.trim()
    const name = clientName.trim()
    if (!url || !name) {
      showSnackbar('Client name and website are required', 'warning')
      return
    }

    setIsAnalyzing(true)
    setAnalysisSummary('')
    dispatch({ type: 'SET_LOADING_AI', payload: true })

    try {
      // Step 1: Analyze website
      showSnackbar('Analyzing website...', 'info')
      const data = await analyzeWebsite(url)
      let resolved = { clientSize, strategyGoal, focusAreas, sherpaNotes }
      if (data) {
        resolved = applyAnalysisData(data)
      }
      setIsAnalyzing(false)

      // Step 2: Create strategy with the resolved values
      showSnackbar('Creating strategy...', 'info')
      const strategy = await createMutation.mutateAsync({
        clientName: name,
        clientSize: resolved.clientSize,
        serviceStage,
        targetPointBudget: targetBudget,
        strategyGoal: resolved.strategyGoal,
        focusAreas: resolved.focusAreas,
        sherpaNotes: resolved.sherpaNotes,
        months: selectedMonths,
        name: `${name} Strategy`,
      } as any)

      dispatch({ type: 'SET_CATALOG', payload: activeCatalog })
      dispatch({ type: 'SET_STRATEGY', payload: strategy })
      dispatch({ type: 'SET_STRATEGY_ID', payload: strategy.strategyId })
      setConfigExpanded(false)

      // Step 3: Get AI recommendations
      showSnackbar('Getting AI recommendations...', 'info')
      const result = await recommendMutation.mutateAsync({ strategyId: strategy.strategyId })
      dispatch({
        type: 'SET_AI_RECOMMENDATIONS',
        payload: { recommendations: result.recommendations, summary: result.strategySummary },
      })

      // Step 4: Auto-accept all recommendations
      const newItems: StrategyItem[] = result.recommendations.map((rec: AiRecommendation) => {
        const catalogItem = activeCatalog.find(c => c.PK === rec.menuItemId)
        return {
          PK: '',
          SK: '',
          itemId: uuidv4(),
          menuItemId: rec.menuItemId,
          taskName: rec.taskName,
          sku: rec.sku,
          points: Number(rec.points) || 0,
          marketValue: Number(catalogItem?.price) || 0,
          serviceLine: catalogItem?.service_line || '',
          department: catalogItem?.subcategory || '',
          category: catalogItem?.category_name || '',
          taCategory: catalogItem?.ta_category || '',
          taType: catalogItem?.ta_type || '',
          deliveryRole: catalogItem?.delivery_role || '',
          templateTaskId: '',
          quantity: 1,
          month: rec.month || '',
          aiRecommended: true,
          aiReasoning: rec.reasoning,
          priority: rec.priority,
          createdAt: new Date().toISOString(),
        }
      })
      if (newItems.length) {
        dispatch({ type: 'ADD_ITEMS', payload: newItems })
        // Persist items to DynamoDB so they're available for deploy
        await addItemsMutation.mutateAsync({ strategyId: strategy.strategyId, items: newItems })
      }

      showSnackbar(
        `Strategy built: ${result.recommendations.length} items selected (${result.totalPoints} pts)`,
        'success'
      )
      if (result.usage) console.log('[Strategy Builder] AI usage:', result.usage)
    } catch (err) {
      console.error('[Strategy Builder] Fetch & Build error:', err)
      showSnackbar('Failed to build strategy', 'error')
    } finally {
      setIsAnalyzing(false)
      dispatch({ type: 'SET_LOADING_AI', payload: false })
    }
  }, [
    clientWebsite,
    clientName,
    clientSize,
    serviceStage,
    targetBudget,
    strategyGoal,
    focusAreas,
    sherpaNotes,
    selectedMonths,
    activeCatalog,
    analyzeWebsite,
    applyAnalysisData,
    createMutation,
    recommendMutation,
    addItemsMutation,
    dispatch,
    showSnackbar,
  ])

  // ── Strategy Create / Save ───────────────────────────────────────────────

  const handleCreateStrategy = useCallback(async () => {
    if (!clientName.trim()) return
    try {
      const strategy = await createMutation.mutateAsync({
        clientName,
        clientSize,
        serviceStage,
        targetPointBudget: targetBudget,
        strategyGoal,
        focusAreas,
        sherpaNotes,
        months: selectedMonths,
        name: `${clientName} Strategy`,
      } as any)
      // Upload any staged files
      if (stagedFiles.length) {
        await Promise.all(
          stagedFiles.map(file => uploadMutation.mutateAsync({ strategyId: strategy.strategyId, file }))
        )
        setStagedFiles([])
      }
      dispatch({ type: 'SET_CATALOG', payload: activeCatalog })
      dispatch({ type: 'SET_STRATEGY', payload: strategy })
      dispatch({ type: 'SET_STRATEGY_ID', payload: strategy.strategyId })
      setConfigExpanded(false)
      showSnackbar('Strategy created', 'success')
    } catch {
      showSnackbar('Failed to create strategy', 'error')
    }
  }, [
    clientName,
    clientSize,
    serviceStage,
    targetBudget,
    strategyGoal,
    focusAreas,
    sherpaNotes,
    selectedMonths,
    activeCatalog,
    stagedFiles,
    createMutation,
    uploadMutation,
    dispatch,
    showSnackbar,
  ])

  // ── AI Recommendations ───────────────────────────────────────────────────

  const handleRecommend = useCallback(async () => {
    if (!strategyId) return
    dispatch({ type: 'SET_LOADING_AI', payload: true })
    try {
      const result = await recommendMutation.mutateAsync({ strategyId })
      dispatch({
        type: 'SET_AI_RECOMMENDATIONS',
        payload: { recommendations: result.recommendations, summary: result.strategySummary },
      })
      if (result.usage) {
        console.log('[Strategy Builder] AI usage:', result.usage)
      }
    } catch {
      showSnackbar('Failed to get AI recommendations', 'error')
    } finally {
      dispatch({ type: 'SET_LOADING_AI', payload: false })
    }
  }, [strategyId, recommendMutation, dispatch, showSnackbar])

  // ── Item toggling ────────────────────────────────────────────────────────

  const toggleItem = useCallback(
    (item: ServiceMenuItem | AiRecommendation) => {
      const id = 'PK' in item ? item.PK : item.menuItemId
      if (selectedIds.has(id)) {
        const existing = selectedItems.find(i => i.menuItemId === id)
        if (existing) dispatch({ type: 'REMOVE_ITEM', payload: existing.itemId })
      } else {
        const catalogItem = 'PK' in item ? item : activeCatalog.find(c => c.PK === id)
        const rec = 'reasoning' in item ? item : null
        const newItem: StrategyItem = {
          PK: '',
          SK: '',
          itemId: uuidv4(),
          menuItemId: id,
          taskName: catalogItem?.title || (rec as any)?.taskName || '',
          sku: catalogItem?.sku || (rec as any)?.sku || '',
          points: Number(catalogItem?.points) || 0,
          marketValue: Number(catalogItem?.price) || 0,
          serviceLine: catalogItem?.service_line || '',
          department: catalogItem?.subcategory || '',
          category: catalogItem?.category_name || '',
          taCategory: catalogItem?.ta_category || '',
          taType: catalogItem?.ta_type || '',
          deliveryRole: catalogItem?.delivery_role || '',
          templateTaskId: '',
          quantity: 1,
          month: rec?.month || '',
          aiRecommended: !!rec,
          aiReasoning: rec?.reasoning || '',
          priority: rec?.priority || 'medium',
          createdAt: new Date().toISOString(),
        }
        dispatch({ type: 'ADD_ITEM', payload: newItem })
      }
    },
    [selectedIds, selectedItems, activeCatalog, dispatch]
  )

  const handleAcceptAll = useCallback(() => {
    const newItems: StrategyItem[] = aiRecommendations
      .filter(rec => !selectedIds.has(rec.menuItemId))
      .map(rec => {
        const catalogItem = activeCatalog.find(c => c.PK === rec.menuItemId)
        return {
          PK: '',
          SK: '',
          itemId: uuidv4(),
          menuItemId: rec.menuItemId,
          taskName: rec.taskName,
          sku: rec.sku,
          points: Number(rec.points) || 0,
          marketValue: Number(catalogItem?.price) || 0,
          serviceLine: catalogItem?.service_line || '',
          department: catalogItem?.subcategory || '',
          category: catalogItem?.category_name || '',
          taCategory: catalogItem?.ta_category || '',
          taType: catalogItem?.ta_type || '',
          deliveryRole: catalogItem?.delivery_role || '',
          templateTaskId: '',
          quantity: 1,
          month: rec.month || '',
          aiRecommended: true,
          aiReasoning: rec.reasoning,
          priority: rec.priority,
          createdAt: new Date().toISOString(),
        }
      })
    if (newItems.length) dispatch({ type: 'ADD_ITEMS', payload: newItems })
  }, [aiRecommendations, selectedIds, activeCatalog, dispatch])

  // ── Save & Deploy ────────────────────────────────────────────────────────

  const [saveNameOpen, setSaveNameOpen] = useState(false)
  const [saveName, setSaveName] = useState('')

  const handleSaveClick = useCallback(() => {
    if (!strategyId || selectedItems.length === 0) return
    // Pre-populate with existing name or generate one
    const defaultName = state.strategy?.name || `${clientName} — ${new Date().toLocaleDateString()}`
    setSaveName(defaultName)
    setSaveNameOpen(true)
  }, [strategyId, selectedItems.length, state.strategy?.name, clientName])

  const handleSaveConfirm = useCallback(async () => {
    setSaveNameOpen(false)
    if (!strategyId || selectedItems.length === 0) return
    try {
      // Clear existing items first to prevent duplicates on re-save
      await axios.delete(`${baseUrl}/api/strategy-builder/${strategyId}/items`).catch(() => {})

      // Save items to DynamoDB
      await addItemsMutation.mutateAsync({ strategyId, items: selectedItems })

      // Calculate totals from selected items
      const totalPoints = selectedItems.reduce((sum: number, item: any) => {
        const pts =
          typeof item === 'string' ? Number(catalog.find(c => c.PK === item)?.points ?? 0) : Number(item.points ?? 0)
        return sum + pts
      }, 0)
      const totalMarketValue = selectedItems.reduce((sum: number, item: any) => {
        const val =
          typeof item === 'string'
            ? Number(catalog.find(c => c.PK === item)?.price ?? 0)
            : Number(item.marketValue ?? item.price ?? 0)
        return sum + val
      }, 0)

      // Update META record with status: 'saved', name, and totals
      await updateStrategyMutation.mutateAsync({
        id: strategyId,
        name: saveName.trim() || `${clientName} Strategy`,
        status: 'saved',
        totalPoints,
        totalMarketValue,
        itemCount: selectedItems.length,
      })

      showSnackbar(`Strategy saved as "${saveName.trim()}" — ${selectedItems.length} items, ${totalPoints} points`, 'success')
    } catch {
      showSnackbar('Failed to save strategy', 'error')
    }
  }, [strategyId, selectedItems, catalog, saveName, clientName, addItemsMutation, updateStrategyMutation, showSnackbar])

  const hasStrategy = !!strategyId

  // Quick-add by service line
  const handleAddByLine = useCallback(
    (line: string) => {
      const lineItems = activeCatalog.filter(i => i.service_line === line && !selectedIds.has(i.PK))
      const newItems: StrategyItem[] = lineItems.map(item => ({
        PK: '',
        SK: '',
        itemId: uuidv4(),
        menuItemId: item.PK,
        taskName: item.title,
        sku: item.sku,
        points: Number(item.points) || 0,
        marketValue: Number(item.price) || 0,
        serviceLine: item.service_line,
        department: item.subcategory || '',
        category: item.category_name || '',
        taCategory: item.ta_category || '',
        taType: item.ta_type || '',
        deliveryRole: item.delivery_role || '',
        templateTaskId: '',
        quantity: 1,
        month: '',
        aiRecommended: false,
        aiReasoning: '',
        priority: 'medium',
        createdAt: new Date().toISOString(),
      }))
      if (newItems.length) dispatch({ type: 'ADD_ITEMS', payload: newItems })
    },
    [activeCatalog, selectedIds, dispatch]
  )

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        pt: pinned ? '60px' : 1,
        transition: 'padding 0.3s ease',
      }}
    >
      {/* ── Top Bar ── */}
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Strategy Builder
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<FolderOpenIcon />} onClick={() => setSavedOpen(true)}>
            Saved
          </Button>
          {hasStrategy && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AutoAwesomeIcon />}
                onClick={handleRecommend}
                disabled={isLoadingAi}
              >
                {isLoadingAi ? 'Thinking...' : 'AI Sherpa'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setSherpaOpen(true)}
              >
                Chat
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSaveClick}
                disabled={selectedItems.length === 0 || addItemsMutation.isPending}
              >
                {addItemsMutation.isPending ? 'Saving...' : `Save (${selectedItems.length})`}
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<RocketLaunchIcon />}
                onClick={() => setDeployOpen(true)}
                disabled={selectedItems.length === 0}
              >
                Deploy to ClickUp
              </Button>
            </>
          )}
        </Stack>
      </Box>

      <Divider />

      {/* ── Main Content (config + items + cart) ── */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* ── Left: Config + Items ── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Strategy Configuration (collapsible) */}
          <Accordion
            expanded={configExpanded}
            onChange={(_, expanded) => setConfigExpanded(expanded)}
            disableGutters
            sx={{ '&:before': { display: 'none' }, bgcolor: 'background.paper' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Strategy Configuration
                </Typography>
                {analysisSummary && (
                  <Chip
                    label="AI enhanced"
                    size="small"
                    icon={<AutoAwesomeIcon />}
                    variant="outlined"
                    color="secondary"
                  />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2 }}>
              <Grid container spacing={2}>
                {/* Client Name + Website */}
                <Grid item xs={12} sm={5}>
                  <Autocomplete
                    freeSolo
                    options={organizations}
                    getOptionLabel={(option: any) => (typeof option === 'string' ? option : option.name || '')}
                    getOptionKey={(option: any) => option.org_uuid || option.PK || option.name || ''}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options
                      const q = inputValue.toLowerCase()
                      return options.filter((o: any) => (o.name || '').toLowerCase().includes(q))
                    }}
                    isOptionEqualToValue={(option: any, value: any) => option.org_uuid === value?.org_uuid}
                    value={
                      organizations.find((o: any) => o.org_uuid === clientOrgId) ||
                      (clientName ? { name: clientName } : null)
                    }
                    onChange={(_, val: any) => {
                      if (val && typeof val !== 'string') {
                        handleClientChange(val.name || '', val.org_uuid || val.PK || null, val.website)
                      } else if (typeof val === 'string') {
                        handleClientChange(val, null)
                      } else {
                        handleClientChange('', null)
                      }
                    }}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input') setClientName(value)
                    }}
                    size="small"
                    renderInput={params => <TextField {...params} label="Client Name" required />}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      label="Client Website"
                      value={clientWebsite}
                      onChange={e => setClientWebsite(e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LanguageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleAnalyzeWebsite()
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!clientWebsite.trim() || isAnalyzing || isLoadingAi}
                      onClick={handleAnalyzeWebsite}
                      sx={{ whiteSpace: 'nowrap', minWidth: 70 }}
                    >
                      {isAnalyzing && !isLoadingAi ? <CircularProgress size={14} /> : 'Fetch'}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!clientWebsite.trim() || !clientName.trim() || isAnalyzing || isLoadingAi}
                      onClick={handleFetchAndBuild}
                      startIcon={
                        isAnalyzing || isLoadingAi ? (
                          <CircularProgress size={14} />
                        ) : (
                          <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                        )
                      }
                      sx={{ whiteSpace: 'nowrap', minWidth: 130 }}
                    >
                      {isAnalyzing ? 'Analyzing...' : isLoadingAi ? 'Building...' : 'Fetch & Build'}
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Points per Month"
                    type="number"
                    value={targetBudget}
                    onChange={e => setTargetBudget(Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>

                {/* Client Size + Stage */}
                <Grid item xs={12} sm={5}>
                  {/* <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Client Size
                  </Typography> */}
                  <ToggleButtonGroup
                    value={clientSize}
                    exclusive
                    onChange={(_, val) => val && setClientSize(val)}
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        flex: 1,
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': { bgcolor: 'primary.dark' },
                        },
                      },
                    }}
                  >
                    {CLIENT_SIZES.map(s => (
                      <ToggleButton key={s} value={s}>
                        {s}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    multiple
                    options={monthOptions}
                    getOptionLabel={o => o.label}
                    value={monthOptions.filter(o => selectedMonths.includes(o.value))}
                    onChange={(_, vals) => setSelectedMonths(vals.map(v => v.value))}
                    size="small"
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index })
                        return <Chip key={key} label={option.label} size="small" {...tagProps} />
                      })
                    }
                    renderInput={params => <TextField {...params} label="Budget Month(s)" />}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Service Stage</InputLabel>
                    <Select
                      value={serviceStage}
                      label="Service Stage"
                      onChange={e => setServiceStage(e.target.value as ServiceStage)}
                    >
                      {SERVICE_STAGES.map(s => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Focus areas */}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Service Line Focus
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {SERVICE_LINE_OPTIONS.map(opt => {
                      const isActive = focusAreas.includes(opt.value)
                      const count = activeCatalog.filter(i => i.service_line === opt.value).length
                      return (
                        <Chip
                          key={opt.value}
                          label={`${opt.label} (${count})`}
                          size="small"
                          variant={isActive ? 'filled' : 'outlined'}
                          color={isActive ? 'primary' : 'default'}
                          onClick={() =>
                            setFocusAreas(prev =>
                              prev.includes(opt.value) ? prev.filter(v => v !== opt.value) : [...prev, opt.value]
                            )
                          }
                          sx={{ cursor: 'pointer' }}
                        />
                      )
                    })}
                  </Box>
                </Grid>

                {/* Strategy goal + Sherpa notes + File upload */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Strategy Goal"
                    value={strategyGoal}
                    onChange={e => setStrategyGoal(e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    placeholder="e.g., Grow SEO maturity, Launch paid media"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Context for AI"
                    value={sherpaNotes}
                    onChange={e => setSherpaNotes(e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    placeholder="Client priorities, things to avoid, special requests..."
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.pdf"
                    multiple
                    hidden
                    onChange={handleFileSelect}
                  />
                  <Box
                    onDragOver={e => {
                      e.preventDefault()
                      setIsDragOver(true)
                    }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={handleFileDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: '1px dashed',
                      borderColor: isDragOver ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      p: 1,
                      textAlign: 'center',
                      cursor: 'pointer',
                      bgcolor: isDragOver ? 'action.hover' : 'transparent',
                      transition: 'all 0.2s ease',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 22, color: 'text.disabled', mb: 0.5 }} />
                    <Typography variant="caption" color="text.secondary">
                      Drag & drop or click
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                      CSV, PDF — context for AI
                    </Typography>
                  </Box>
                  {stagedFiles.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {stagedFiles.map((file, idx) => (
                        <Box
                          key={`${file.name}-${idx}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            py: 0.25,
                            px: 0.5,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <DescriptionIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography
                            variant="caption"
                            sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {file.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={e => {
                              e.stopPropagation()
                              removeStagedFile(idx)
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Grid>

                {/* Create button */}
                {!hasStrategy && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        onClick={handleCreateStrategy}
                        disabled={!clientName.trim() || createMutation.isPending}
                      >
                        {createMutation.isPending ? 'Creating...' : 'Create Strategy'}
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Divider />

          {/* ── Menu Items Browser ── */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1 }}>
            {/* AI Recommendations (if available) */}
            {aiRecommendations.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    AI Recommendations ({aiRecommendations.length})
                  </Typography>
                  <Button size="small" startIcon={<CheckIcon />} onClick={handleAcceptAll}>
                    Accept All
                  </Button>
                </Box>
                {aiSummary && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {aiSummary}
                  </Typography>
                )}
                <Stack spacing={0.5} sx={{ mb: 2 }}>
                  {aiRecommendations.map(rec => {
                    const isSelected = selectedIds.has(rec.menuItemId)
                    return (
                      <Box
                        key={rec.menuItemId}
                        onClick={() => toggleItem(rec)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 0.75,
                          px: 1.5,
                          borderRadius: 1,
                          cursor: 'pointer',
                          bgcolor: isSelected ? 'action.selected' : 'transparent',
                          border: 1,
                          borderColor: isSelected ? 'success.main' : 'divider',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <AutoAwesomeIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: isSelected ? 600 : 400 }}>
                            {rec.taskName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {rec.sku} | {rec.points} pts | {rec.reasoning}
                          </Typography>
                        </Box>
                        <Chip
                          label={rec.priority}
                          size="small"
                          color={PRIORITY_COLORS[rec.priority]}
                          variant="outlined"
                        />
                        <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 30, textAlign: 'right' }}>
                          {rec.points}
                        </Typography>
                      </Box>
                    )
                  })}
                </Stack>
                <Divider sx={{ mb: 1 }} />
              </Box>
            )}

            {isLoadingAi && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  AI is building recommendations...
                </Typography>
              </Box>
            )}

            {/* Menu Items Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Menu Items ({filteredCatalog.length})
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {activeCatalog.length} total
                </Typography>
              </Typography>
            </Box>

            {/* Search + Filters */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                placeholder="Search items..."
                value={search}
                onChange={e => { setSearch(e.target.value); setVisibleCount(50) }}
                size="small"
                fullWidth
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} displayEmpty>
                  {departments.map(d => (
                    <MenuItem key={d} value={d}>
                      {d}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <Select value={lineFilter} onChange={e => setLineFilter(e.target.value)} displayEmpty>
                  {serviceLines.map(l => (
                    <MenuItem key={l} value={l}>
                      {l}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Quick add by service line */}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
              <Chip
                label="Select All Visible"
                size="small"
                variant="outlined"
                onClick={() =>
                  filteredCatalog.forEach(i => {
                    if (!selectedIds.has(i.PK)) toggleItem(i)
                  })
                }
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                label="Clear All"
                size="small"
                variant="outlined"
                onClick={() => dispatch({ type: 'SET_SELECTED_ITEMS', payload: [] })}
                sx={{ cursor: 'pointer' }}
              />
              {SERVICE_LINE_OPTIONS.filter(o => focusAreas.includes(o.value) || focusAreas.length === 0)
                .slice(0, 6)
                .map(opt => (
                  <Chip
                    key={opt.value}
                    label={`+ ${opt.label}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => handleAddByLine(opt.value)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
            </Box>

            {/* Item list */}
            <Stack spacing={0}>
              {visibleCatalog.map(item => {
                const isSelected = selectedIds.has(item.PK)
                return (
                  <Box
                    key={item.PK + item.SK}
                    onClick={() => hasStrategy && toggleItem(item)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.75,
                      px: 1,
                      borderRadius: 1,
                      cursor: hasStrategy ? 'pointer' : 'default',
                      bgcolor: isSelected ? 'action.selected' : 'transparent',
                      '&:hover': hasStrategy ? { bgcolor: 'action.hover' } : {},
                      opacity: hasStrategy ? 1 : 0.6,
                    }}
                  >
                    {isSelected && <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />}
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        flexShrink: 0,
                        bgcolor: SERVICE_LINE_OPTIONS.find(o => o.value === item.service_line)
                          ? theme.palette.primary.main
                          : 'text.disabled',
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: isSelected ? 600 : 400 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {item.sku && (
                          <Chip label={item.sku} size="small" sx={{ height: 16, fontSize: '0.6rem', mr: 0.5 }} />
                        )}
                        {item.service_line} · {item.subcategory || item.category_name}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {item.points}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ${item.price}
                      </Typography>
                    </Box>
                  </Box>
                )
              })}
              {filteredCatalog.length > visibleCount && (
                <Button
                  size="small"
                  onClick={() => setVisibleCount(prev => prev + 50)}
                  sx={{ mt: 1, alignSelf: 'center' }}
                >
                  Show More ({filteredCatalog.length - visibleCount} remaining)
                </Button>
              )}
            </Stack>
          </Box>
        </Box>

        {/* ── Right: Strategy Cart ── */}
        <Box
          sx={{
            width: 280,
            flexShrink: 0,
            borderLeft: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Strategy Cart
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {totalPoints}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Points
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  ${totalValue.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  MV Value
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {selectedItems.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Items
                </Typography>
              </Box>
            </Stack>
            {targetBudget > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Budget: {totalPoints}/{targetBudget} pts
                  <Typography
                    component="span"
                    sx={{
                      ml: 0.5,
                      fontWeight: 600,
                      color:
                        Math.abs(totalPoints - targetBudget) <= targetBudget * 0.1 ? 'success.main' : 'warning.main',
                    }}
                  >
                    ({totalPoints - targetBudget > 0 ? '+' : ''}
                    {totalPoints - targetBudget})
                  </Typography>
                </Typography>
              </Box>
            )}
          </Box>

          <Divider />

          {/* Selected items list */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 1 }}>
            {selectedItems.length === 0 ? (
              <Typography variant="caption" color="text.disabled" sx={{ p: 2, display: 'block', textAlign: 'center' }}>
                No items selected yet
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {selectedItems.map(item => (
                  <Box
                    key={item.itemId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                      bgcolor: 'background.default',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" noWrap sx={{ fontWeight: 500 }}>
                        {item.taskName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {item.points} pts
                        {item.aiRecommended && ' · AI'}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation()
                        dispatch({ type: 'REMOVE_ITEM', payload: item.itemId })
                      }}
                    >
                      <RemoveIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {/* Deploy button at bottom of cart */}
          {hasStrategy && selectedItems.length > 0 && (
            <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
              <Button
                variant="contained"
                fullWidth
                size="small"
                startIcon={<RocketLaunchIcon />}
                onClick={() => setDeployOpen(true)}
              >
                Deploy to ClickUp
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Save Name Dialog */}
      <Dialog open={saveNameOpen} onClose={() => setSaveNameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Name Your Strategy</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Strategy Name"
            value={saveName}
            onChange={e => setSaveName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSaveConfirm() }}
            sx={{ mt: 1 }}
            helperText="This name will appear in the Saved Strategies browser"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSaveNameOpen(false)} size="small">Cancel</Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveConfirm}
            disabled={!saveName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <StrategyDeployDialog open={deployOpen} onClose={() => setDeployOpen(false)} />
      <SavedStrategiesDialog open={savedOpen} onClose={() => setSavedOpen(false)} onLoad={handleLoadSaved} />
      <AISherpaDialog
        open={sherpaOpen}
        onClose={() => setSherpaOpen(false)}
        strategyId={strategyId}
        cartItems={selectedItems}
        config={{
          clientName,
          clientSize,
          serviceStage,
          targetPointBudget: targetBudget,
          strategyGoal,
          focusAreas,
          sherpaNotes,
          months: selectedMonths,
        }}
        recommendations={aiRecommendations}
      />
    </Box>
  )
}

const StrategyBuilder = () => (
  <BuilderProvider>
    <StrategyBuilderInner />
  </BuilderProvider>
)

export default StrategyBuilder
